import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import SkillTreeNode from './SkillTreeNode';
import { SkillTreeTier } from './skillTreeData';
import { useColors } from '../../theme/ColorModeContext';


interface Stats {
    challenges: number;
    solved: number;
}

interface SkillTreeProps {
    tiers: SkillTreeTier[];
    stats: Record<string, Stats>;
    token: string;
}

const SkillTree = ({ tiers, stats, token }: SkillTreeProps) => {
    const colors = useColors();
    return (
        <Box sx={{ width: '100%' }}>
            {tiers.map((tier, tierIdx) => (
                    <Box
                        key={tierIdx}
                        sx={{
                            mb: { xs: '32px', md: '48px' },
                            animation: `fadeInUp 0.5s ease-out ${tierIdx * 0.12}s both`,
                        }}
                    >
                        {/* Tier label centered above cards */}
                        <Typography variant="caption" sx={{
                            display: 'block',
                            textAlign: 'center',
                            color: colors.text.muted,
                            fontFamily: '"Orbitron", sans-serif',
                            textTransform: 'uppercase',
                            letterSpacing: '0.12em',
                            fontSize: '0.7rem',
                            mb: 0.5,
                        }}>
                            {tier.label}
                        </Typography>

                        {/* Tier description below label */}
                        <Typography variant="body2" sx={{
                            background: colors.gradient.primary,
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            fontSize: '0.9rem',
                            lineHeight: 1.5,
                            textAlign: 'center',
                            fontFamily: '"Orbitron", sans-serif',
                            fontWeight: 500,
                            mb: 2,
                            px: 2,
                        }}>
                            {tier.description}
                        </Typography>

                        {/* Cards row */}
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: { xs: 'center', md: 'flex-start' },
                            gap: { xs: '20px', md: '40px' },
                            flexDirection: { xs: 'column', md: 'row' },
                        }}>
                            {tier.nodes.map((node, nodeIdx) => (
                                <SkillTreeNode
                                    key={node.slug}
                                    node={node}
                                    stats={stats[node.slug]}
                                    locked={node.requiresAuth && !token}
                                    animateDelay={tierIdx * 0.12 + nodeIdx * 0.05}
                                />
                            ))}
                        </Box>
                    </Box>
            ))}
        </Box>
    );
};

export default SkillTree;
